import { Defaults } from "./DefaultStateSchema";
import { OutOfStock } from "@/ProductPage/components/LabelTag";

interface _coded {
  code: string;
}

interface _Product extends _coded {
  description: string | null;
  label: string;
  priceTableLabel: string | null;
  defaultStrength: string;
  actionButtonType: string;
  medicineType: string;
  tag: Tag | null;
  strengths: _Strength[];
}

export interface Products {
  currentProduct: string;
  products: Map<string, Product>;
}

export interface Product extends _coded {
  currentStrength: string;
  description: string | null;
  inStock: boolean;
  actionButtonType: string;
  label: string;
  priceTableLabel: string | null;
  defaultStrength: string;
  tag: Tag | null;
  medicineType: string;
  strengths: Map<string, Strength>;
}

interface _SubscriptionFrequency extends _coded {
  label: string;
  quantities: Quantity[];
}

export interface SubscriptionFrequency extends _coded {
  label: string;
  currentQuantity: string;
  quantities: Map<string, Quantity>;
}

interface Tag {
  text: string;
  type: string;
}

interface _Strength extends _coded {
  description: string | null;
  label: string;
  quantities: Quantity[];
  tag: Tag | null;
  inStock: boolean;
  subscriptionFrequencies: _SubscriptionFrequency[];
}

export interface Strength extends _coded {
  currentOTPQuantity: string;
  currentFrequency: string | undefined;
  description: string | null;
  label: string;
  tag: Tag | null;
  inStock: boolean;
  quantities: Map<string, Quantity>;
  subscriptionFrequencies: Map<string, SubscriptionFrequency>;
}

export interface Discount {
  discountSaving: string;
  priceAfterDiscount: number;
  discountCode: string;
  newCustomerOnly: boolean;
}

export interface Quantity extends _coded {
  label: string;
  available: boolean;
  price: number;
  unit: string;
  discount: Discount | null;
}

export function load(obj: _Product[], defaults: Defaults): Products {
  const products: _Product[] = obj;

  return {
    currentProduct: defaults.defaultProductCode || pickCurrentProduct(products),
    products: createCodeMap(products.map((p) => createProduct(p, defaults))),
  };
}

function pickCurrentProduct(products: _Product[]): string {
  return products.find((product) => isProductInStock(product))?.code || products[0].code;
}

function isProductInStock(product: _Product): boolean {
  return product.strengths.some((strength) => strength.inStock);
}

function createProduct(p: _Product, defaults: Defaults): Product {
  return {
    code: p.code,
    currentStrength: (p.code == defaults.defaultProductCode && defaults.defaultDosageCode) || p.defaultStrength,
    description: p.description,
    inStock: isProductInStock(p),
    label: p.label,
    priceTableLabel: p.priceTableLabel,
    defaultStrength: p.defaultStrength,
    actionButtonType: p.actionButtonType,
    medicineType: p.medicineType,
    tag: createOutOfStockTag({ inStock: isProductInStock(p) }) || p.tag,
    strengths: createCodeMap(p.strengths.map((s) => createStrength(p, s, defaults))),
  };
}

function createStrength(p: _Product, s: _Strength, defaults: Defaults): Strength {
  return {
    code: s.code,
    currentFrequency:
      defaults.defaultProductCode || defaults.defaultDosageCode || defaults.defaultQuantityCode
        ? undefined
        : s.subscriptionFrequencies[0]?.code,
    currentOTPQuantity:
      defaults.defaultQuantityCode &&
      s.quantities.find((quantity) => quantity.code == defaults.defaultQuantityCode) != undefined
        ? defaults.defaultQuantityCode
        : s.quantities[0].code,
    description: s.description,
    label: s.label,
    tag: createOutOfStockTag(s) || s.tag,
    inStock: s.inStock,
    quantities: createCodeMap(s.quantities),
    subscriptionFrequencies: createCodeMap(s.subscriptionFrequencies.map(createSubscriptionFrequency)),
  };
}

function createSubscriptionFrequency(f: _SubscriptionFrequency) {
  return {
    code: f.code,
    label: f.label,
    currentQuantity: f.quantities[0].code,
    quantities: createCodeMap(f.quantities),
  };
}

function createOutOfStockTag(stockTracked: { inStock: boolean }): Tag | undefined {
  if (stockTracked.inStock) {
    return undefined;
  }

  return {
    text: "OUT OF STOCK",
    type: OutOfStock,
  };
}

function createCodeMap<T extends _coded>(cs: T[]): Map<string, T> {
  const map: Map<string, T> = new Map();
  cs.forEach((c) => map.set(c.code, c));
  return map;
}
